import "./side-menu-items.component.scoped.scss";
import React, { useState, useContext, useMemo, useCallback } from "react";
import { history } from "react-router-guard";
import { useTranslation } from "react-i18next";
import { sideMenuItems } from "../../types/sidenav-items";
import { GlobalContext } from "../../../../store";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { config } from "../../../../config";
import { Box, Typography } from "@material-ui/core";
import { User } from "../../../../types/user";
import {
  ChildMenuItemProps,
  MenuItemProps,
  SideNavChild,
  SideNavComponent,
  MenuSectionProps,
} from "../../types/side-menu.types";

// Utility function to check if user has access to a menu item
const hasAccess = (roles: string[] | undefined, user: User): boolean => {
  if (!roles || roles.length === 0) {
    return true; // No roles specified means accessible to all
  }
  const userRole = user.role?.toUpperCase() || "";
  return roles.some((role) => role === userRole);
};

const MenuItem: React.FC<MenuItemProps> = ({
  component,
  isActive,
  isHovered,
  fullNav,
  onNavigate,
  onMouseEnter,
  onMouseLeave,
  onToggleSubItems,
  openSubItems,
  t,
}) => {
  const iconSrc = isActive || isHovered ? component.activeIcon : component.icon;

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`side__menu__item ${isActive ? "active" : ""}`}
      onClick={() => {
        onNavigate(component);
        console.log(component);
      }}
    >
      <img className="nav-icon" alt={component.name} src={iconSrc} />
      <Box className={`nav-text ${fullNav ? "" : "hide-text"}`}>
        {t(component.name)}
        {component.children && onToggleSubItems && fullNav && (
          <Box
            className="expand__subitems"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSubItems();
            }}
          >
            {openSubItems ? <RemoveIcon /> : <AddIcon />}
          </Box>
        )}
        {component.type === "link" && <ChevronRightIcon />}
      </Box>
    </Box>
  );
};

const ChildMenuItem: React.FC<ChildMenuItemProps> = ({
  child,
  isActive,
  isHovered,
  fullNav,
  onNavigate,
  onMouseEnter,
  onMouseLeave,
  t,
}) => {
  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`side__menu__item ${isActive ? "active" : ""}`}
      onClick={() => onNavigate(child)}
    >
      <Box className={`nav-text ${fullNav ? "" : "hide-text"}`}>
        {t(child.name)}
      </Box>
    </Box>
  );
};

const MenuSection: React.FC<MenuSectionProps> = ({
  item,
  fullNav,
  routeName,
  user,
  itemHover,
  openSubItems,
  onNavigate,
  onSetItemHover,
  onToggleSubItems,
  t,
}) => {
  // Filter components based on user roles
  const visibleComponents = useMemo(
    () =>
      item.components.filter((component) => hasAccess(component.roles, user)),
    [item.components, user],
  );

  if (visibleComponents.length === 0) {
    return null;
  }

  return (
    <Box className="side__menu__component">
      {item.name && (
        <Typography
          variant="body1"
          style={{
            color: "#636674",
            transition: "all 0.3s",
            textTransform: "capitalize",
          }}
          className={fullNav ? "" : "hide-text"}
        >
          {t(item.name)}
        </Typography>
      )}
      {visibleComponents.map((component, componentIndex) => {
        const isActive =
          (routeName.includes(component.path || "") && !component.children) ||
          (routeName.includes(component.path || "") &&
            component.children &&
            !fullNav);

        const isHovered = itemHover === component.name;

        return (
          <React.Fragment key={componentIndex}>
            <MenuItem
              component={component}
              isActive={isActive}
              isHovered={isHovered}
              fullNav={fullNav}
              onNavigate={onNavigate}
              onMouseEnter={() => onSetItemHover(component.name)}
              onMouseLeave={() => onSetItemHover("")}
              onToggleSubItems={
                component.children ? onToggleSubItems : undefined
              }
              openSubItems={openSubItems}
              t={t}
            />
            {component.children && component.children.length > 0 && fullNav && (
              <Box
                className={`side__menu__item__children ${
                  openSubItems ? "expand__items" : ""
                }`}
              >
                {component.children
                  .filter((child) => hasAccess(child.roles, user))
                  .map((child, childIndex) => {
                    const isChildActive = routeName.includes(child.path);
                    const isChildHovered = itemHover === child.name;

                    return (
                      <ChildMenuItem
                        key={childIndex}
                        child={child}
                        isActive={isChildActive}
                        isHovered={isChildHovered}
                        fullNav={fullNav}
                        onNavigate={onNavigate}
                        onMouseEnter={() => onSetItemHover(child.name)}
                        onMouseLeave={() => onSetItemHover("")}
                        t={t}
                      />
                    );
                  })}
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export const SideMenuItemsComponent: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { fullNav, user, setFullNav } = useContext(GlobalContext);

  const routeName = history.location.pathname;
  const [openSubItems, setOpenSubItems] = useState(false);
  const [itemHover, setItemHover] = useState<string>("");

  // Memoize filtered menu items based on user roles
  const filteredMenuItems = useMemo(
    () =>
      sideMenuItems.filter((item) =>
        item.components.some((component: SideNavComponent) =>
          hasAccess(component.roles, user),
        ),
      ),
    [user],
  );

  const openNav = useCallback(() => {
    setFullNav(true);
    // Use CSS classes instead of direct DOM manipulation
    document.body.classList.add("full-nav");
  }, [setFullNav]);

  const closeNav = useCallback(() => {
    setFullNav(false);
    document.body.classList.remove("full-nav");
  }, [setFullNav]);

  const navigate = useCallback(
    (item: SideNavComponent | SideNavChild) => {
      console.log(item);
      if (!item.path) {
        if ((item as SideNavComponent).children && !fullNav) {
          setFullNav(true);
          setOpenSubItems(true);
        }
        return;
      }

      if ((item as SideNavComponent).type === "link") {
        window.open(item.path, "_blank");
      } else {
        history.push(item.path);
      }
    },
    [fullNav, setFullNav],
  );

  const toggleSubItems = useCallback(() => {
    setOpenSubItems((prev) => !prev);
  }, []);

  return (
    <Box className={`side__menu__items ${fullNav ? "full" : ""}`}>
      {filteredMenuItems.map((item, itemIndex) => (
        <MenuSection
          key={itemIndex}
          item={item}
          fullNav={fullNav}
          routeName={routeName}
          user={user}
          itemHover={itemHover}
          openSubItems={openSubItems}
          onNavigate={navigate}
          onSetItemHover={setItemHover}
          onToggleSubItems={toggleSubItems}
          t={t}
        />
      ))}
      <Box className="expand">
        {!fullNav && <ChevronRightIcon onClick={openNav} />}
        {fullNav && (
          <Box className="expand-full" onClick={closeNav}>
            <ChevronLeftIcon />
            <Box className="nav-text">{t("collapse")}</Box>
          </Box>
        )}
      </Box>
      <Box className="version">
        <p>V{config.appVersion}</p>
      </Box>
    </Box>
  );
};
